import { useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../app";
import {
  EventTimer,
  withEventTiming,
  withSyncEventTiming,
  createEventTimer,
} from "./timer";
import { UserProperties } from "./eventInterface";
import packageJson from "../../../../package.json";

/**
 * React hook that provides event timing functionality with automatic user context
 */
export const useEventTimer = () => {
  const user = useSelector((state: RootState) => state.applicationState.user);
  const activeTimers = useRef<Map<string, EventTimer>>(new Map());

  // Get user context for amplitude logging
  const getUserContext = useCallback((): {
    userAndCompany: string;
    userProperties: UserProperties;
  } => {
    if (!user) {
      return {
        userAndCompany: "unknown@unknown",
        userProperties: { company: "unknown", user: "unknown" },
      };
    }

    return {
      userAndCompany:
        user.userNameAndCompany || `${user.userName}@${user.company}`,
      userProperties: {
        company: user.company || "unknown",
        user: user.userName || "unknown",
      },
    };
  }, [user]);

  /**
   * Start a timer for an event
   */
  const startTimer = useCallback(
    (eventName: string, title: string, componentName: string): EventTimer => {
      const { userAndCompany, userProperties } = getUserContext();

      const timer = createEventTimer(
        eventName,
        userAndCompany,
        userProperties,
        title,
        componentName,
        packageJson.version
      );

      activeTimers.current.set(eventName, timer);
      return timer;
    },
    [getUserContext]
  );

  /**
   * Complete a timer and log to amplitude
   */
  const completeTimer = useCallback((eventName: string): void => {
    const timer = activeTimers.current.get(eventName);
    if (timer) {
      timer.complete();
      activeTimers.current.delete(eventName);
    } else {
      console.warn(`No active timer found for event: ${eventName}`);
    }
  }, []);

  /**
   * Wrap an async function with timing
   */
  const timeAsyncEvent = useCallback(
    async <T>(
      eventName: string,
      title: string,
      componentName: string,
      fn: () => Promise<T>
    ): Promise<T> => {
      const { userAndCompany, userProperties } = getUserContext();

      return withEventTiming(
        eventName,
        userAndCompany,
        userProperties,
        title,
        componentName,
        packageJson.version,
        fn
      );
    },
    [getUserContext]
  );

  /**
   * Wrap a sync function with timing
   */
  const timeSyncEvent = useCallback(
    <T>(
      eventName: string,
      title: string,
      componentName: string,
      fn: () => T
    ): T => {
      const { userAndCompany, userProperties } = getUserContext();

      return withSyncEventTiming(
        eventName,
        userAndCompany,
        userProperties,
        title,
        componentName,
        packageJson.version,
        fn
      );
    },
    [getUserContext]
  );

  /**
   * Get elapsed time for an active timer
   */
  const getElapsedTime = useCallback((eventName: string): number | null => {
    const timer = activeTimers.current.get(eventName);
    return timer ? timer.getElapsedTime() : null;
  }, []);

  /**
   * Check if a timer is active
   */
  const isTimerActive = useCallback((eventName: string): boolean => {
    return activeTimers.current.has(eventName);
  }, []);

  /**
   * Clear all active timers
   */
  const clearAllTimers = useCallback((): void => {
    activeTimers.current.clear();
  }, []);

  return {
    startTimer,
    completeTimer,
    timeAsyncEvent,
    timeSyncEvent,
    getElapsedTime,
    isTimerActive,
    clearAllTimers,
  };
};
